import React, {useCallback, useEffect, useRef} from "react";
import translate from "components/translations/translations.wrapper";
import {useDispatch, useSelector} from "react-redux";
import * as reduxSelectors from "components/store/application.reducers";
import {Store} from "redux";
import {changeZoomPercentage} from "components/taskdetails-remake/taskDetails.action";
import _ from "lodash";
import {snowplowTrackEvent} from "utils/snowplow";
import {getArchiveCallbackUrl} from "utils/api/api";
import {launchDarkly} from "utils/launchDarkly";
import {TextInput} from "components/common/TextInput.component";
import {Button, ButtonType} from "components/common/Button.component";
import {Maximize2, DownloadIcon, Printer, Plus, Minus} from "lucide-react";
import {ICON_SIZE} from "components/common/constants";

const MAX_ZOOM = 300;
const MIN_ZOOM = 50;

export const Menu = ({
                         totalImages,
                         rotateImage,
                         pageInView,
                         scrollToImage,
                         translate,
                         zoomPercentage,
                         changeZoom,
                         hasZoomToFit = true
                     }: any): React.ReactElement => {

    const downloadElement = useRef(null);

    const dispatch = useDispatch();

    const downloadLinks = useSelector((store: Store) => reduxSelectors.getAttachmentsDownloadLinks(store));
    const menuRef = React.useRef<HTMLDivElement>(null);
    const zoomInputRef = React.useRef<HTMLInputElement>(null);
    const pageInViewRef = React.useRef<HTMLInputElement>(null);
    const isPreviewDetached = useSelector((store: Store) => reduxSelectors.isDocumentPreviewDetached(store));
    const userData = useSelector((store: Store) => reduxSelectors.getUsersData(store));

    const useNewUrlsForArchive = launchDarkly.userClient?.variation("fe-use-new-urls-for-archive", false);

    const handlePageChange = _.debounce((event: React.ChangeEvent<HTMLInputElement>) => {
        let value = parseInt(event.target.value, 10);
        if (!value) return;
        if (value > 0 && value <= totalImages) {
            scrollToImage(value);
        }

        if (pageInViewRef.current)
            pageInViewRef.current.value = event.target.value;
    }, 300);

    const handleChangeZoom = (event: React.ChangeEvent<HTMLInputElement>) => {
        let value = parseInt(event.target.value, 10);
        if (!value) return;
        if (zoomInputRef.current)
            zoomInputRef.current.value = value.toString();

        if (value >= 50 && value <= 300) {
            dispatch(changeZoom(value));
        }
    }

    const handleZoomOut = () => {
        let newZoom = (zoomPercentage - 20 >= MIN_ZOOM) ? zoomPercentage - 20 : MIN_ZOOM;
        if (newZoom !== zoomPercentage) {
            dispatch(changeZoom(newZoom));
        }
    }

    const handleZoomIn = () => {
        let newZoom = (zoomPercentage + 20 <= MAX_ZOOM) ? zoomPercentage + 20 : MAX_ZOOM;
        if (newZoom !== zoomPercentage) {
            dispatch(changeZoom(newZoom));
        }
    }

    const handleZoomToFit = () => {
        dispatch(changeZoomPercentage(100));
    }

    useEffect(() => {
        if (zoomInputRef.current)
            zoomInputRef.current.value = Math.ceil(zoomPercentage).toString();
    }, [zoomPercentage, zoomInputRef]);

    useEffect(() => {
        if (pageInViewRef.current)
            pageInViewRef.current.value = pageInView.toString();
    }, [pageInView, pageInViewRef]);

    const downloadZIP = () => {
        if (isPreviewDetached) {
            snowplowTrackEvent({
                category: "task_detail",
                action: "detach-window",
                label: "download-attachment",
                value: "true",
            }, userData);
        }
        return false;
    }

    const downloadArchiveUsingCallbackFactory = (archiveType: string, link: string | null) => {
        return () => {
            if (isPreviewDetached) {
                snowplowTrackEvent({
                    category: "task_detail",
                    action: "detach-window",
                    label: "download-attachment",
                    value: "true",
                }, userData);
            }
            // Message: preparing <archiveType>..
            getArchiveCallbackUrl(link).then((result: { url: string; }) => {
                if (result.url != null && result.url !== "") {
                    downloadElement.current.href = result.url;
                    // Message: <archiveType> is ready, click to download it
                    downloadElement.current.click();
                } else {
                    // Message: the <archiveType> file could not be generated
                }
            });
            return false;
        }
    }

    const downloadZIPUsingCallback = downloadArchiveUsingCallbackFactory("zip", downloadLinks.downloadZIPCallbackUrl);
    const downloadPDFUsingCallback = downloadArchiveUsingCallbackFactory("pdf", downloadLinks.downloadPDFCallbackUrl);

    const printPDF = () => {
        if (isPreviewDetached) {
            snowplowTrackEvent({
                category: "task_detail",
                action: "detach-window",
                label: "print-attachment",
                value: "true",
            }, userData);
        }
        return false;
    }


    return (
        <div
            className={`attachments-panel__images__menu d-flex bg-white mt-8 px-16 align-items-center ${totalImages > 0 ? "justify-content-between" : "justify-content-center"}`}
            role={"menu"} ref={menuRef}>
            <a href="" target="_blank" download="" hidden={true} ref={downloadElement}></a>

            {totalImages > 0 &&
                <div className={"d-flex align-items-center"}>
                    <div className={"mr-2"}>{translate("taskDetail.panels.attachments.page")}</div>
                    <div className={"mx-2"}>
                        <TextInput
                            id={"page-in-view"}
                            type="number"
                            ref={pageInViewRef}
                            className={`page-in-view-input manual-zoom-input"}`}
                            onChange={handlePageChange}
                            min={1}
                            max={totalImages} step={1}/>
                    </div>
                    <span className={"mx-2"}>{translate("taskDetail.panels.attachments.of")}</span>
                    <span> {totalImages || 0}</span>
                </div>
            }


            <div className={"d-flex align-items-center"}>
                <span className={"mx-2"}>{translate("taskDetail.panels.attachments.zoom")}</span>

                <Button type={ButtonType.GHOST}
                        onClick={handleZoomOut}
                        iconOnly={true}
                        icon={<Minus size={ICON_SIZE.MEDIUM}/>}
                        title={translate("taskDetail.zoomOut")}
                />

                <div className={"mx-2"}>
                    <TextInput
                        id={"zoom-input"}
                        type="number"
                        ref={zoomInputRef}
                        className={`manual-zoom-input "}`}
                        onChange={useCallback(handleChangeZoom, [dispatch, changeZoom])}
                        min={MIN_ZOOM}
                        max={MAX_ZOOM} step={1}/>
                </div>

                <Button type={ButtonType.GHOST}
                        onClick={handleZoomIn}
                        iconOnly={true}
                        icon={<Plus size={ICON_SIZE.MEDIUM}/>}
                        title={translate("taskDetail.zoomIn")}
                />


            </div>

            <div className={"d-flex justify-content-between align-items-center"}>
                {rotateImage &&
                    <div className={"d-flex align-items-center"}>

                        <Button type={ButtonType.GHOST}
                                onClick={rotateImage}
                                iconOnly={false}
                                title={translate("taskDetail.rotateRight")}
                                text={translate("taskDetail.rotateRight")}
                        />
                        {hasZoomToFit &&
                            <Button type={ButtonType.GHOST}
                                    onClick={handleZoomToFit}
                                    iconOnly={true}
                                    title={translate("taskDetail.zoomToFit")}
                                    icon={<Maximize2 size={ICON_SIZE.MEDIUM}/>}
                                    dataTestId={"__tst_maximize"}
                            />
                        }
                    </div>
                }

                <div className={`d-flex align-items-center ${(totalImages > 0) ? " " : " position-absolute right-0"}`}>
                    {(useNewUrlsForArchive && downloadLinks.downloadZIPCallbackUrl && downloadLinks.downloadZIPCallbackUrl !== "") ?
                        <a className="details__action-button me-2"
                           target={isPreviewDetached ? "_blank" : "_self"}
                           href="javascript:void(0)"
                           onClick={downloadZIPUsingCallback}
                           title={translate("taskDetail.download")}>
                            <DownloadIcon/>
                        </a> : (downloadLinks.downloadZIPUrl && downloadLinks.downloadZIPUrl !== "" &&
                            <a className="details__action-button me-2"
                               href={downloadLinks.downloadZIPUrl}
                               target={isPreviewDetached ? "_blank" : "_self"}
                               onClick={downloadZIP}
                               title={translate("taskDetail.download")}>
                                <DownloadIcon/>
                            </a>
                        )}

                    {useNewUrlsForArchive && downloadLinks.downloadPDFCallbackUrl && downloadLinks.downloadPDFCallbackUrl !== "" ?
                        <a className="details__action-button me-2"
                           target={isPreviewDetached ? "_blank" : "_self"}
                           href="javascript:void(0)"
                           onClick={downloadPDFUsingCallback}
                           title={translate("taskDetail.print")}>
                            <Printer/>
                        </a> : (downloadLinks.downloadPDFUrl && downloadLinks.downloadPDFUrl !== "" &&
                            <a className={"details__action-button me-3"}
                               href={downloadLinks.downloadPDFUrl} target='blank'
                               onClick={printPDF}
                               title={translate("taskDetail.print")}>
                                <Printer/>
                            </a>)}
                </div>
            </div>
        </div>
    )
}

export default translate(Menu);
